import React, { useState } from 'react';
import SectionTitle from '../components/ui/SectionTitle';

interface FormData {
  companyName: string;
  businessType: 'selbststaendig' | 'unternehmen' | '';
  industry: string;
  employeeCount: '0' | '1-5' | '6-20' | '21-50' | '50+' | '';
  hasLiabilityInsurance: 'Ja' | 'Nein' | 'Unsicher' | '';
  hasEmployeeInsurance: 'Ja' | 'Nein' | 'Unsicher' | '';
  hasHealthInsurance: 'Ja' | 'Nein' | 'Unsicher' | '';
  hasRetirementPlan: 'Ja' | 'Nein' | 'Unsicher' | '';
  hasAssetCoverage: 'Ja' | 'Nein' | 'Unsicher' | '';
  hasEmployeeBenefits: 'Ja' | 'Nein' | 'Unsicher' | '';
  hasInsuranceOverview: 'Ja' | 'Nein' | 'Unsicher' | '';
  email: string;
  phone: string;
  additionalInfo: string;
  acceptTerms: boolean;
}

const initialFormData: FormData = {
  companyName: '',
  businessType: '',
  industry: '',
  employeeCount: '',
  hasLiabilityInsurance: '',
  hasEmployeeInsurance: '',
  hasHealthInsurance: '',
  hasRetirementPlan: '',
  hasAssetCoverage: '',
  hasEmployeeBenefits: '',
  hasInsuranceOverview: '',
  email: '',
  phone: '',
  additionalInfo: '',
  acceptTerms: false,
};

const FORM_NAME = 'unternehmens-check';

const UnternehmensCheck: React.FC = () => {
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const target = e.target;
    const { name, type } = target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? (target as HTMLInputElement).checked : target.value,
    }));
  };

  const calculateScore = () => {
    const questions: Array<keyof FormData> = [
      'hasLiabilityInsurance',
      'hasEmployeeInsurance',
      'hasHealthInsurance',
      'hasRetirementPlan',
      'hasAssetCoverage',
      'hasEmployeeBenefits',
      'hasInsuranceOverview',
    ];

    return questions.reduce((score, question) => {
      return score + (formData[question] === 'Ja' ? 1 : 0);
    }, 0);
  };


  const encode = (data: Record<string, string>) => {
    return Object.keys(data)
      .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(data[key])}`)
      .join('&');
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!formData.acceptTerms) {
      setError('Bitte stimmen Sie den Datenschutzbestimmungen zu.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const payload: Record<string, string> = {
        'form-name': FORM_NAME,
        subject: 'Neue Anfrage: Unternehmens-Check',
        companyName: formData.companyName,
        businessType: formData.businessType,
        industry: formData.industry,
        employeeCount: formData.employeeCount,
        hasLiabilityInsurance: formData.hasLiabilityInsurance,
        hasEmployeeInsurance: formData.hasEmployeeInsurance,
        hasHealthInsurance: formData.hasHealthInsurance,
        hasRetirementPlan: formData.hasRetirementPlan,
        hasAssetCoverage: formData.hasAssetCoverage,
        hasEmployeeBenefits: formData.hasEmployeeBenefits,
        hasInsuranceOverview: formData.hasInsuranceOverview,
        insuranceScore: String(calculateScore()),
        email: formData.email,
        phone: formData.phone,
        additionalInfo: formData.additionalInfo,
        acceptTerms: formData.acceptTerms ? 'Ja' : 'Nein',
      };

      await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: encode(payload),
      });

      setSubmitted(true);
      setFormData(initialFormData);
      setTimeout(() => {
        setSubmitted(false);
      }, 5000);
    } catch (err) {
      console.error('Netlify form submission error:', err);
      setError(
        'Fehler beim Senden des Formulars. Bitte versuchen Sie es später erneut oder kontaktieren Sie uns direkt.'
      );

      setTimeout(() => {
        setError(null);
      }, 8000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const insuranceQuestions: Array<{ id: keyof FormData; question: string }> = [
    {
      id: 'hasLiabilityInsurance',
      question:
        'Haben Sie eine Betriebshaftpflicht- oder Vermögensschadenhaftpflichtversicherung?',
    },
    {
      id: 'hasEmployeeInsurance',
      question:
        'Ist die Arbeitskraft Ihrer Mitarbeiter abgesichert (BU, Grundfähigkeit, Dread Disease)?',
    },
    {
      id: 'hasHealthInsurance',
      question: 'Entspricht die Krankenversicherung der aktuellen Geschäftssituation?',
    },
    {
      id: 'hasRetirementPlan',
      question: 'Haben Sie eine Altersvorsorgeplanung?',
    },
    {
      id: 'hasAssetCoverage',
      question: 'Sind Ihre Sachwerte (IT, Gebäude, Inventar) ausreichend abgesichert?',
    },
    {
      id: 'hasEmployeeBenefits',
      question: 'Nutzen Sie steuerlich geförderte Modelle zur Mitarbeiterbindung (bKV / bAV)?',
    },
    {
      id: 'hasInsuranceOverview',
      question: 'Haben Sie einen Überblick über alle bestehenden Versicherungen?',
    },
  ];

  return (
    <>
      <section className="pt-32 pb-16 bg-gray-light">
        <div className="container-custom">
          <h1 className="font-bold mb-4">Unternehmens-Check</h1>
          <p className="text-xl text-gray-600">
            Prüfen Sie jetzt mit wenigen Klicks, wie gut Ihr Unternehmen aktuell abgesichert ist.
          </p>
        </div>
      </section>

      <section className="section">
        <div className="container-custom max-w-3xl">
          <form
            name={FORM_NAME}
            method="POST"
            data-netlify="true"
            netlify-honeypot="bot-field"
            onSubmit={handleSubmit}
            className="space-y-8"
          >
            <input type="hidden" name="form-name" value={FORM_NAME} />
            <input type="hidden" name="subject" value="Neue Anfrage: Unternehmens-Check" />
            <input type="hidden" name="insuranceScore" value={String(calculateScore())} />

            <p className="hidden">
              <label>
                Nicht ausfüllen: <input name="bot-field" onChange={() => undefined} />
              </label>
            </p>

            {submitted && (
              <div className="bg-green-100 text-green-800 p-4 rounded-md mb-6">
                Vielen Dank für Ihre Anfrage. Wir melden uns zeitnah bei Ihnen.
              </div>
            )}

            {error && (
              <div className="bg-red-100 text-red-800 p-4 rounded-md mb-6">
                {error}
              </div>
            )}

            <div className="space-y-6">
              <SectionTitle title="Unternehmensinformationen" />

              <div className="bg-white p-6 rounded-lg shadow-sm">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Name / Unternehmen*
                    </label>
                    <input
                      type="text"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Unternehmensform*
                    </label>
                    <select
                      name="businessType"
                      value={formData.businessType}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                    >
                      <option value="">Bitte wählen</option>
                      <option value="selbststaendig">Selbstständig</option>
                      <option value="unternehmen">Unternehmen</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Branche
                    </label>
                    <input
                      type="text"
                      name="industry"
                      value={formData.industry}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Anzahl der Mitarbeiter*
                    </label>
                    <select
                      name="employeeCount"
                      value={formData.employeeCount}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                    >
                      <option value="">Bitte wählen</option>
                      <option value="0">0</option>
                      <option value="1-5">1–5</option>
                      <option value="6-20">6–20</option>
                      <option value="21-50">21–50</option>
                      <option value="50+">Über 50</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <SectionTitle title="Versicherungs-Check" />

              {insuranceQuestions.map((q) => (
                <div key={q.id} className="bg-white p-6 rounded-lg shadow-sm">
                  <p className="font-medium mb-3">{q.question}</p>
                  <div className="flex gap-4 flex-wrap">
                    {['Ja', 'Nein', 'Unsicher'].map((option) => (
                      <label key={option} className="flex items-center">
                        <input
                          type="radio"
                          name={q.id}
                          value={option}
                          checked={formData[q.id] === option}
                          onChange={handleChange}
                          required
                          className="mr-2"
                        />
                        {option}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="space-y-6">
              <SectionTitle title="Kontaktdaten" />

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    E-Mail*
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Telefon*
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Zusätzliche Informationen
                  </label>
                  <textarea
                    name="additionalInfo"
                    value={formData.additionalInfo}
                    onChange={handleChange}
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                  />
                </div>
              </div>
            </div>

            <div className="flex items-start space-x-2">
              <input
                type="checkbox"
                name="acceptTerms"
                checked={formData.acceptTerms}
                onChange={handleChange}
                required
                className="mt-1"
              />
              <label className="text-sm text-gray-600">
                Ich stimme der Verarbeitung meiner Daten gemäß Datenschutzbestimmungen zu.
              </label>
            </div>

            <div className="flex gap-4">
              <button
                type="submit"
                className="btn-primary w-full"
                disabled={!formData.acceptTerms || isSubmitting}
              >
                {isSubmitting ? 'Wird gesendet...' : 'Unternehmens-Check absenden'}
              </button>
            </div>
          </form>
        </div>
      </section>
    </>
  );
};

export default UnternehmensCheck;